import { useState } from "react";
import { api, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ABSENCE_TYPES } from "@/lib/employeeConstants";

export const AbsencesTab = ({ employee, stats, absences, year, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    absence_type: "cp", start_date: "", end_date: "", reason: "", is_half_day: false, half_day_period: null, start_time: "", end_time: "",
  });

  const handleSave = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error("Dates obligatoires");
      return;
    }
    try {
      await api.addAbsence(employee.id, form);
      toast.success("Absence ajoutée");
      setDialogOpen(false);
      setForm({ absence_type: "cp", start_date: "", end_date: "", reason: "", is_half_day: false, half_day_period: null, start_time: "", end_time: "" });
      onRefresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async (id) => {
    await api.deleteAbsence(id);
    toast.success("Supprimé");
    onRefresh();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Congés payés</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-sky-600">{stats.balances.cp.remaining}</div>
            <div className="text-xs text-muted-foreground">restants / {stats.balances.cp.initial}</div>
            <div className="text-xs mt-2 text-muted-foreground">Pris : {stats.balances.cp.used} j</div>
            <div className="text-xs text-muted-foreground">Période : 1er avril → 31 mars</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">RTT salarié</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-cyan-600">{stats.balances.rtt_salarie.remaining}</div>
            <div className="text-xs text-muted-foreground">restants sur <span className="font-semibold">{stats.balances.rtt_salarie.accrued}</span> acquis</div>
            <div className="text-xs mt-2 text-muted-foreground">Pris : {stats.balances.rtt_salarie.used} j</div>
            <div className="text-xs text-muted-foreground">Total annuel : {stats.balances.rtt_salarie.annual_total} j</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">RTT entreprise</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-teal-600">{stats.balances.rtt_entreprise.remaining}</div>
            <div className="text-xs text-muted-foreground">restants sur <span className="font-semibold">{stats.balances.rtt_entreprise.accrued}</span> acquis</div>
            <div className="text-xs mt-2 text-muted-foreground">Pris : {stats.balances.rtt_entreprise.used} j</div>
            <div className="text-xs text-muted-foreground">Total annuel : {stats.balances.rtt_entreprise.annual_total} j</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Statistiques d'absences {year}</CardTitle>
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="add-absence-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {Object.entries(ABSENCE_TYPES).map(([k, v]) => (
              <div key={k} className="border rounded-lg p-2 text-center">
                <div className="text-xs text-muted-foreground">{v.label}</div>
                <div className="text-lg font-semibold">{stats.absences.by_type[k]?.days || 0} j</div>
                <div className="text-xs text-muted-foreground">({stats.absences.by_type[k]?.count || 0} abs.)</div>
              </div>
            ))}
            <div className="border rounded-lg p-2 text-center bg-primary/5">
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="text-lg font-bold">{stats.absences.total.days} j</div>
              <div className="text-xs text-muted-foreground">({stats.absences.total.count} abs.)</div>
            </div>
          </div>
          {absences.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Aucune absence enregistrée.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>Jours</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {absences.map(a => (
                  <TableRow key={a.id}>
                    <TableCell><Badge className={ABSENCE_TYPES[a.absence_type]?.color}>{ABSENCE_TYPES[a.absence_type]?.label}</Badge></TableCell>
                    <TableCell>{formatDate(a.start_date)}</TableCell>
                    <TableCell>{formatDate(a.end_date)}</TableCell>
                    <TableCell>{a.days_count}</TableCell>
                    <TableCell>{a.reason || "-"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} data-testid={`del-abs-${a.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle absence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.absence_type} onValueChange={v => setForm({ ...form, absence_type: v })}>
                <SelectTrigger data-testid="abs-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ABSENCE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Début</Label>
                <Input
                  type="date" value={form.start_date}
                  onChange={e => setForm({
                    ...form, start_date: e.target.value,
                    is_half_day: e.target.value !== form.end_date ? false : form.is_half_day
                  })}
                  data-testid="abs-start"
                />
              </div>
              <div>
                <Label>Fin</Label>
                <Input
                  type="date" value={form.end_date}
                  onChange={e => setForm({
                    ...form, end_date: e.target.value,
                    is_half_day: form.start_date !== e.target.value ? false : form.is_half_day
                  })}
                  data-testid="abs-end"
                />
              </div>
            </div>
            {form.start_date && form.start_date === form.end_date && (
              <div className="flex flex-col gap-2 p-2 bg-muted/40 rounded-md">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox" id="abs-half-day"
                    checked={!!form.is_half_day}
                    onChange={e => setForm({ ...form, is_half_day: e.target.checked, half_day_period: e.target.checked ? (form.half_day_period || "morning") : null })}
                    data-testid="abs-half-day"
                  />
                  <label htmlFor="abs-half-day" className="text-sm cursor-pointer select-none">Demi-journée (0.5 jour)</label>
                </div>
                {form.is_half_day && (
                  <div className="flex items-center gap-3 pl-6">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="radio" name="abs-period"
                        checked={form.half_day_period === "morning"}
                        onChange={() => setForm({ ...form, half_day_period: "morning" })}
                        data-testid="abs-half-morning"
                      /> Matin
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="radio" name="abs-period"
                        checked={form.half_day_period === "afternoon"}
                        onChange={() => setForm({ ...form, half_day_period: "afternoon" })}
                        data-testid="abs-half-afternoon"
                      /> Après-midi
                    </label>
                  </div>
                )}
              </div>
            )}
            {/* Horaires précis pour intempéries / justifiée / injustifiée */}
            {["intemperies", "justifiee", "injustifiee"].includes(form.absence_type) && (
              <div className="grid grid-cols-2 gap-3 p-2 bg-blue-50/50 border border-blue-100 rounded-md">
                <div>
                  <Label className="text-xs">Heure début (optionnel)</Label>
                  <Input
                    type="time" value={form.start_time || ""}
                    onChange={e => setForm({ ...form, start_time: e.target.value })}
                    data-testid="abs-start-time"
                  />
                </div>
                <div>
                  <Label className="text-xs">Heure fin (optionnel)</Label>
                  <Input
                    type="time" value={form.end_time || ""}
                    onChange={e => setForm({ ...form, end_time: e.target.value })}
                    data-testid="abs-end-time"
                  />
                </div>
              </div>
            )}
            <div>
              <Label>Raison / commentaire</Label>
              <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} data-testid="save-absence-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AbsencesTab;
